/**
 * Performance Drivers (APP-2005): what the API sends, and the pure helpers the
 * section uses. The figures come from the Python service's
 * `POST /performance/drivers` (`python/docs/performance_drivers_api.md`); every
 * return, weight and contribution is a fraction, so 0.0597 is 5.97%.
 */

export type DriversPeriodId =
  | "ytd"
  | "last_year"
  | "m3"
  | "m6"
  | "y1"
  | "y3"
  | "y5"
  | "inception"
  | "custom";

export type DriversGroupBy = "asset_class" | "category" | "position";

export type DriversPeriod = {
  id: DriversPeriodId;
  start: string;
  end: string;
  available: boolean;
  hasBenchmark: boolean;
};

export type DriversPosition = {
  key: string;
  symbol: string;
  description: string;
  avgWeight: number | null;
  return: number | null;
  contribution: number | null;
  share: number | null;
  soldBy: string | null;
};

export type DriversGroup = {
  name: string;
  avgWeight: number | null;
  return: number | null;
  contribution: number | null;
  positions: DriversPosition[];
};

export type DriversExcludedAccount = {
  accountId: number;
  name: string;
  displayNumber: string | null;
};

export type PerformanceDrivers = {
  asOfDate: string;
  historyStart: string | null;
  periods: DriversPeriod[];
  period: DriversPeriod;
  groupBy: DriversGroupBy;
  /** Accounts with no BridgeFT ID (manually entered), left out of the figures. */
  excludedAccounts: DriversExcludedAccount[];
  total?: {
    return: number | null;
    annualizedReturn: number | null;
    contributionTotal: number | null;
    feesAndTiming: number | null;
    isOfficial: boolean;
  };
  benchmark?: {
    /** The blend has no return for the period, or one dated other than the data date. */
    unavailable: boolean;
    return: number | null;
    annualizedReturn: number | null;
    difference: number | null;
    asOf: string | null;
  } | null;
  groups?: DriversGroup[];
  positions?: DriversPosition[];
};

export type BlendPart = { symbol: string; weight: number };

/** The Period list, in the order of the existing Performance selector. */
export const DRIVERS_PERIODS: { id: DriversPeriodId; label: string }[] = [
  { id: "ytd", label: "YTD" },
  { id: "last_year", label: "Last Year" },
  { id: "m3", label: "3 Months" },
  { id: "m6", label: "6 Months" },
  { id: "y1", label: "1 Year" },
  { id: "y3", label: "3 Years" },
  { id: "y5", label: "5 Years" },
  { id: "inception", label: "Since Inception" },
  { id: "custom", label: "Custom" },
];

export const GROUP_BY_OPTIONS: { id: DriversGroupBy; label: string }[] = [
  { id: "asset_class", label: "Asset Class" },
  { id: "category", label: "Category" },
  { id: "position", label: "Positions" },
];

export function periodLabel(id: DriversPeriodId) {
  return DRIVERS_PERIODS.find((p) => p.id === id)?.label ?? id;
}

/** `ACWI:60,AGG:40`, the `benchmark` query parameter. */
export function blendToParam(parts: BlendPart[]) {
  return parts.map((p) => `${p.symbol.toUpperCase()}:${p.weight}`).join(",");
}

export function blendDescription(parts: BlendPart[]) {
  return parts.map((p) => `${p.weight}% ${p.symbol.toUpperCase()}`).join(" / ");
}

/** The tickers the API accepts, as in `performance-drivers.service.ts`. */
const TICKER = /^[A-Z0-9.\-^]{1,12}$/;

/**
 * A blend can be used once its funds are named, no fund is listed twice and
 * its weights add up to 100.
 */
export function blendIsValid(parts: BlendPart[]) {
  const tickers = parts.map((p) => p.symbol.trim().toUpperCase());
  return (
    parts.length > 0 &&
    parts.every((p, i) => TICKER.test(tickers[i]) && p.weight > 0) &&
    new Set(tickers).size === tickers.length &&
    Math.abs(parts.reduce((sum, p) => sum + p.weight, 0) - 100) < 1e-9
  );
}

export type DriversQuery = {
  period: DriversPeriodId;
  groupBy: DriversGroupBy;
  startDate?: string;
  endDate?: string;
  /** Household only; leave out for the whole household. */
  accountIds?: number[];
  benchmark?: BlendPart[];
};

/** The path of the household or account route, with its query string. */
export function performanceDriversPath(
  kind: "households" | "accounts",
  id: number,
  query: DriversQuery,
) {
  const params = new URLSearchParams({
    period: query.period,
    "group-by": query.groupBy,
  });
  if (query.period === "custom") {
    if (query.startDate) params.set("start-date", query.startDate);
    if (query.endDate) params.set("end-date", query.endDate);
  }
  if (kind === "households" && query.accountIds?.length) {
    params.set("account-ids", query.accountIds.join(","));
  }
  if (query.benchmark?.length && blendIsValid(query.benchmark)) {
    params.set("benchmark", blendToParam(query.benchmark));
  }
  return `/performance-drivers/${kind}/${id}?${params.toString()}`;
}

/** One row of the chart or table: a group, or a position in Positions view. */
export type DriverRow = {
  key: string;
  name: string;
  detail?: string;
  avgWeight: number | null;
  return: number | null;
  contribution: number;
  soldBy?: string | null;
  positions?: DriversPosition[];
};

export function driverRows(data: PerformanceDrivers): DriverRow[] {
  if (data.groupBy === "position" || !data.groups?.length) {
    return (data.positions ?? []).map((p) => ({
      key: p.key,
      name: p.symbol || p.description,
      detail: p.description,
      avgWeight: p.avgWeight,
      return: p.return,
      contribution: p.contribution ?? 0,
      soldBy: p.soldBy,
    }));
  }
  return data.groups.map((g) => ({
    key: g.name,
    name: g.name,
    avgWeight: g.avgWeight,
    return: g.return,
    contribution: g.contribution ?? 0,
    positions: g.positions,
  }));
}

/**
 * A contribution smaller than this rounds to 0.00%: it neither helped nor held
 * back, so it is never listed as either.
 */
export const NOTICEABLE_CONTRIBUTION = 0.00005;

/**
 * The rows that helped most (largest first) and held back most (largest drag
 * first), up to `count` each. The client view, At a Glance and the talking
 * points all use this, so they agree on which rows count.
 */
export function helpedAndHurt(rows: DriverRow[], count: number) {
  const helped = rows
    .filter((r) => r.contribution >= NOTICEABLE_CONTRIBUTION)
    .sort((a, b) => b.contribution - a.contribution)
    .slice(0, count);
  const hurt = rows
    .filter((r) => r.contribution <= -NOTICEABLE_CONTRIBUTION)
    .sort((a, b) => a.contribution - b.contribution)
    .slice(0, count);
  return { helped, hurt };
}

/**
 * The client portal view: the top three that helped and the top three that
 * hurt, with the rest folded into one "Everything else" row.
 */
export function clientRows(rows: DriverRow[]) {
  const byContribution = (a: DriverRow, b: DriverRow) =>
    b.contribution - a.contribution;
  const { helped, hurt } = helpedAndHurt(rows, 3);
  const shown = new Set([...helped, ...hurt]);
  return {
    top: rows.filter((r) => shown.has(r)).sort(byContribution),
    other: rows.filter((r) => !shown.has(r)).sort(byContribution),
  };
}

function signedPercent(value: number) {
  // Round first, so a tiny figure reads 0.00% rather than −0.00%.
  const rounded = Math.round(value * 10000) / 10000;
  const text = `${(Math.abs(rounded) * 100).toFixed(2)}%`;
  return rounded > 0 ? `+${text}` : rounded < 0 ? `−${text}` : text;
}

export { signedPercent };

function formatDate(iso: string) {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export { formatDate as formatDriversDate };

const PERIOD_PHRASES: Record<DriversPeriodId, string> = {
  ytd: "the year to date",
  last_year: "last year",
  m3: "the past three months",
  m6: "the past six months",
  y1: "the past year",
  y3: "the past three years",
  y5: "the past five years",
  inception: "the period since inception",
  custom: "the period",
};

function join(items: string[]) {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * The paragraph an advisor can paste into a reply to a client. Advisor view
 * only: never shown on the client portal.
 */
export function talkingPoints(
  data: PerformanceDrivers,
  scope: "household" | "accounts" | "account",
  benchmarkName?: string,
) {
  if (!data.total || data.total.return == null) return "";
  const rows = driverRows(data);
  const name = (r: DriverRow) =>
    data.groupBy === "position" && r.detail
      ? `${r.detail} (${r.name})`
      : r.name;
  const largest = helpedAndHurt(rows, 2);
  const describe = (r: DriverRow) =>
    `${name(r)} (${signedPercent(r.contribution)})`;
  const helped = largest.helped.map(describe);
  const hurt = largest.hurt.map(describe);

  const subject =
    scope === "household"
      ? "the portfolio"
      : scope === "account"
        ? "the account"
        : "these accounts";
  const dates = `${formatDate(data.period.start)} – ${formatDate(data.period.end)}`;
  let text = `Over ${PERIOD_PHRASES[data.period.id]} (${dates}), ${subject} returned ${signedPercent(data.total.return)}`;
  if (
    !data.benchmark?.unavailable &&
    data.benchmark?.return != null &&
    benchmarkName
  ) {
    text += `, compared with ${signedPercent(data.benchmark.return)} for the ${benchmarkName} benchmark`;
  }
  text += ".";
  if (data.total.return < 0) {
    if (hurt.length) text += ` The main drags were ${join(hurt)}.`;
    if (helped.length) text += ` ${join(helped)} helped offset part of that.`;
  } else {
    if (helped.length)
      text += ` The biggest contributors were ${join(helped)}.`;
    if (hurt.length) text += ` ${join(hurt)} held results back.`;
  }
  return text;
}

function csvCell(value: string | number | null | undefined) {
  if (value == null) return "";
  // A name starting with = + - @ would run as a formula in a spreadsheet.
  const text =
    typeof value === "number"
      ? (value * 100).toFixed(4)
      : /^[=+\-@\t\r]/.test(value)
        ? `'${value}`
        : value;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The Detail table as CSV: percentages on the 0 to 100 scale. */
export function driversCsv(data: PerformanceDrivers) {
  const header = [
    "Group",
    "Symbol",
    "Description",
    "Avg Weight %",
    "Return %",
    "Contribution %",
    "Sold By",
  ];
  const lines: (string | number | null | undefined)[][] = [];
  if (data.groupBy === "position" || !data.groups?.length) {
    for (const p of data.positions ?? []) {
      lines.push([
        "",
        p.symbol,
        p.description,
        p.avgWeight,
        p.return,
        p.contribution,
        p.soldBy,
      ]);
    }
  } else {
    for (const g of data.groups) {
      lines.push([g.name, "", "", g.avgWeight, g.return, g.contribution, ""]);
      for (const p of g.positions) {
        lines.push([
          g.name,
          p.symbol,
          p.description,
          p.avgWeight,
          p.return,
          p.contribution,
          p.soldBy,
        ]);
      }
    }
  }
  if (data.total) {
    lines.push([
      "Fees & cash-flow timing",
      "",
      "",
      null,
      null,
      data.total.feesAndTiming,
      "",
    ]);
    lines.push(["Total", "", "", 1, data.total.return, data.total.return, ""]);
  }
  return [header, ...lines].map((row) => row.map(csvCell).join(",")).join("\n");
}

export function csvFileName(name: string, data: PerformanceDrivers) {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${slug}-performance-drivers-${data.period.id}-${data.period.end}.csv`;
}

/** `Roth (…1234)`, or the name alone when the account has no display number. */
export function excludedAccountLabel(account: DriversExcludedAccount) {
  return account.displayNumber
    ? `${account.name} (${account.displayNumber})`
    : account.name;
}

/** True when every selected account is an excluded account (R46). */
export function allAccountsExcluded(
  excluded: DriversExcludedAccount[] | undefined,
  selectedAccountIds: number[],
) {
  if (!excluded?.length || selectedAccountIds.length === 0) return false;
  const ids = new Set(excluded.map((a) => a.accountId));
  return selectedAccountIds.every((accountId) => ids.has(accountId));
}

/** The R45 line naming the excluded accounts, or undefined when there are none. */
export function excludedAccountsNote(
  excluded: DriversExcludedAccount[] | undefined,
) {
  if (!excluded?.length) return undefined;
  return `Left out of these figures: ${excluded.map(excludedAccountLabel).join(", ")}.`;
}

export const NO_BRIDGEFT_RETURN =
  "BridgeFT has no return for this selection and period.";
